import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Copy, Eye, EyeOff, Info } from 'lucide-react';
import { CreateApiKeyInput, useCreateApiKey } from '../../api/hooks/apikeys/useApiKeyMutation';

interface ApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ApiKeyModal: React.FC<ApiKeyModalProps> = ({ isOpen, onClose }) => {
  const [showKey, setShowKey] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm<CreateApiKeyInput>();
  const createApiKey = useCreateApiKey();

  const onSubmit = async (data: CreateApiKeyInput) => {
    try {
      console.log('[Create API Key] Form data before processing:', data);
      
      // Convert datetime-local to ISO 8601 format
      if (data.expiresAt) {
        data.expiresAt = new Date(data.expiresAt).toISOString();
      }
      
      // Convert comma-separated IPs to array if provided
      if (typeof data.ipWhitelist === 'string') {
        const ipString = data.ipWhitelist as any;
        if (ipString.trim() === '') {
          // Empty string should be undefined or empty array
          delete data.ipWhitelist;
        } else {
          data.ipWhitelist = ipString
            .split(',')
            .map((ip: string) => ip.trim())
            .filter((ip: string) => ip.length > 0);
        }
      }

      console.log('[Create API Key] Sending data:', data);
      const result = await createApiKey.mutateAsync(data);
      setCreatedKey(result.key);
    } catch (error) {
      console.error('Failed to create API key:', error);
    }
  };

  const handleCopy = async () => {
    if (createdKey) {
      await navigator.clipboard.writeText(createdKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    reset();
    setCreatedKey(null);
    setShowKey(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-4">
          {createdKey ? 'API Key Created' : 'Create New API Key'}
        </h2>

        {createdKey ? (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start space-x-3">
              <Info className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-semibold mb-1">Important: Save this API key securely!</p>
                <p>This key will not be shown again. If you lose it, you'll need to create a new one.</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your API Key
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type={showKey ? 'text' : 'password'}
                  value={createdKey}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-white font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowKey(!showKey)}
                  className="p-2 text-gray-500 hover:text-gray-700"
                >
                  {showKey ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2"
                >
                  <Copy size={16} />
                  <span>{copied ? 'Copied!' : 'Copy'}</span>
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('name', { required: 'Name is required' })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                placeholder="e.g., Desktop App Production"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Expiration Date <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                {...register('expiresAt', { required: 'Expiration date is required' })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              />
              {errors.expiresAt && (
                <p className="mt-1 text-sm text-red-600">{errors.expiresAt.message}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Set an expiration date for this API key
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Rate Limit (requests per hour) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                {...register('rateLimit', { 
                  required: 'Rate limit is required',
                  valueAsNumber: true,
                  min: { value: 1, message: 'Rate limit must be at least 1' }
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                placeholder="1000"
              />
              {errors.rateLimit && (
                <p className="mt-1 text-sm text-red-600">{errors.rateLimit.message}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Number of requests allowed per hour
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                IP Whitelist (Optional)
              </label>
              <input
                type="text"
                {...register('ipWhitelist' as any)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                placeholder="192.168.1.1, 10.0.0.0/24"
              />
              <p className="mt-1 text-sm text-gray-500">
                Comma-separated list of allowed IP addresses or CIDR ranges
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createApiKey.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {createApiKey.isPending ? 'Creating...' : 'Create API Key'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};